<main class="detail">
  <div class="detail__container">
    @if (loading()) {
      <!-- Skeleton estructurado: header + 3 líneas + bloque de totales. Mantiene
           layout estable durante la carga (sin saltos) y nunca deja pantalla en blanco. -->
      <div class="detail__skeleton" role="status" aria-label="Cargando tu pedido">
        <div class="detail__skeleton-header">
          <div class="detail__skel detail__skel--title"></div>
          <div class="detail__skel detail__skel--badge"></div>
        </div>
        <div class="detail__skeleton-block">
          @for (i of [0, 1, 2]; track i) {
            <div class="detail__skeleton-row">
              <div class="detail__skel detail__skel--thumb"></div>
              <div class="detail__skel detail__skel--line"></div>
            </div>
          }
        </div>
        <div class="detail__skeleton-block">
          <div class="detail__skel detail__skel--line"></div>
          <div class="detail__skel detail__skel--line"></div>
        </div>
      </div>
    } @else if (notFound()) {
      <div class="detail__not-found">
        <img
          class="detail__mascot"
          [src]="notFoundMascotUrl"
          alt=""
          loading="lazy"
        />
        <h1 class="detail__title">Pedido no encontrado</h1>
        <p class="detail__subtitle">
          El enlace puede haber expirado o no ser válido. Si crees que esto es
          un error, contáctanos por nuestro chat.
        </p>
        <app-button variant="primary" routerLink="/tienda">
          Volver a la tienda
        </app-button>
      </div>
    } @else if (preorder(); as p) {
      <header class="detail__header">
        <div class="detail__header-top">
          <div class="detail__header-left">
            <p class="detail__pretitle">Pedido</p>
            <h1 class="detail__title">#{{ p.id }}</h1>
            @if (p.date) {
              <p class="detail__meta">
                Realizado el {{ p.date | date: 'longDate' }}
              </p>
            }
          </div>
          <app-badge [variant]="stateBadgeVariant(p.state)">
            {{ stateLabel(p.state) }}
          </app-badge>
        </div>
        <p class="detail__subtitle">
          Detalles enviados a <strong>{{ p.email }}</strong>.
        </p>
      </header>

      @if (p.lines && p.lines.length > 0) {
        <section class="detail__lines" aria-label="Productos del pedido">
          <h2 class="detail__section-title">Productos</h2>
          <ul class="detail__lines-list">
            @for (line of p.lines; track line.id) {
              <li class="detail__line">
                @if (deliveryIcon(line); as icon) {
                  <span class="detail__line-icon" aria-hidden="true">
                    <app-icon [icon]="icon" [size]="28" />
                  </span>
                } @else {
                  <img
                    class="detail__line-thumb"
                    [src]="lineImageUrl(line)"
                    [alt]="line.name"
                    loading="lazy"
                    (error)="onImageError($event)"
                  />
                }
                <div class="detail__line-info">
                  <p class="detail__line-name">{{ line.name }}</p>
                  @if (line.reference && !deliveryIcon(line)) {
                    <p class="detail__line-ref">{{ line.reference }}</p>
                  }
                  <p class="detail__line-qty">
                    Cantidad: <span>{{ line.quantity }}</span>
                  </p>
                </div>
                <div class="detail__line-amounts">
                  <p class="detail__line-unit">
                    {{ toAmount(line.sale_price) | crc }} c/u
                  </p>
                  <p class="detail__line-total">
                    {{ toAmount(line.total) | crc }}
                  </p>
                </div>
              </li>
            }
          </ul>
        </section>
      }

      <section class="detail__summary" aria-label="Resumen y pagos">
        <h2 class="detail__section-title">Resumen</h2>
        <dl class="detail__summary-rows">
          @if (p.subtotal !== undefined && p.subtotal !== null) {
            <div class="detail__summary-row">
              <dt>Subtotal</dt>
              <dd>{{ toAmount(p.subtotal) | crc }}</dd>
            </div>
          }
          @if (toAmount(p.discount) > 0) {
            <div class="detail__summary-row">
              <dt>
                Descuento
                @if (p.promotional_code; as promo) {
                  <span class="detail__promo-tag">({{ promo.code }})</span>
                }
              </dt>
              <dd>−{{ toAmount(p.discount) | crc }}</dd>
            </div>
          }
          @if (toAmount(p.taxes) > 0) {
            <div class="detail__summary-row">
              <dt>Impuestos</dt>
              <dd>{{ toAmount(p.taxes) | crc }}</dd>
            </div>
          }
          @if (toAmount(p.delivery_price) > 0) {
            <div class="detail__summary-row">
              <dt>Envío</dt>
              <dd>{{ toAmount(p.delivery_price) | crc }}</dd>
            </div>
          }
          <div class="detail__summary-row detail__summary-row--total">
            <dt>Total</dt>
            <dd>{{ toAmount(p.total) | crc }}</dd>
          </div>
        </dl>

        @if (p.payments && p.payments.length > 0) {
          <div class="detail__payments">
            <h3 class="detail__subsection-title">Pagos registrados</h3>
            <ul class="detail__payments-list">
              @for (payment of p.payments; track payment.id) {
                <li class="detail__payment-row">
                  <span>{{ payment.created_at | date: 'mediumDate' }}</span>
                  <span>{{ toAmount(payment.amount) | crc }}</span>
                </li>
              }
            </ul>
          </div>
        }

        @if (isFullyPaid()) {
          <p class="detail__balance detail__balance--paid">Pagado completo</p>
        } @else if (balanceDue() > 0) {
          <p class="detail__balance detail__balance--due">
            Saldo pendiente: <strong>{{ balanceDue() | crc }}</strong>
          </p>
        }
      </section>

      <section class="detail__client" aria-label="Datos de contacto y entrega">
        <h2 class="detail__section-title">Datos de contacto y entrega</h2>
        <dl class="detail__details">
          @if (p.client; as c) {
            <div class="detail__detail-row">
              <dt>Cliente</dt>
              <dd>{{ c.name }} {{ c.surnames }}</dd>
            </div>
          }
          <div class="detail__detail-row">
            <dt>Correo</dt>
            <dd>{{ p.email }}</dd>
          </div>
          @if (p.phone) {
            <div class="detail__detail-row">
              <dt>Teléfono</dt>
              <dd>{{ p.phone }}</dd>
            </div>
          }
          <div class="detail__detail-row">
            <dt>Método de envío</dt>
            <dd>
              @switch (p.delivery_method) {
                @case ('ps') { Retirar en Perrón Store }
                @case ('uber') { Mensajero por tu cuenta (Uber Flash, etc.) }
                @case ('other_shop') {
                  Envío a otra tienda
                  @if (p.shop_name) { ({{ p.shop_name }}) }
                }
                @case ('home') { Correos de Costa Rica }
                @case ('ps_delivery') { Mensajero de Perrón Store }
              }
            </dd>
          </div>
          @if (fullAddress(p); as address) {
            <div class="detail__detail-row">
              <dt>Dirección</dt>
              <dd>{{ address }}</dd>
            </div>
          }
        </dl>
      </section>

      @if (p.comments) {
        <section class="detail__comments" aria-label="Comentarios">
          <h2 class="detail__section-title">Comentarios</h2>
          <p class="detail__comments-text">{{ p.comments }}</p>
        </section>
      }

      <div class="detail__actions">
        <app-button variant="primary" routerLink="/tienda">
          Volver a la tienda
        </app-button>
      </div>
    }
  </div>
</main>
